import React, { useState } from "react";
import "../../App.css";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { LogIn } from "./Schema";
import { useFormik } from "formik";
import { lightAuthTheme, darkAuthTheme } from "../../Theme/authTheme";
import { ThemeProvider } from "@mui/material";
import { useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import { getUser } from "../../Redux/Actions/UserActions";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Button, InputAdornment, IconButton, Paper } from "@mui/material";
import { toast } from "react-toastify";
import AuthLogo from "./AuthLogo";
import { useTheme } from "../../contexts/ThemeContext";
import { signInNew, respondMfaNew } from "../../utils/cognitoAuth";
import { getUserDetails } from "../../utils/srpLogin";
import { recover2fa } from "../../utils/restAPI";

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  // Holds the MFA challenge context between the password step and the code step.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [usingBackupCode, setUsingBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [backupSubmitting, setBackupSubmitting] = useState(false);

  // Choose the appropriate theme based on current mode
  const authTheme = isDarkMode ? darkAuthTheme : lightAuthTheme;

  const handleSignUpClick = () => {
    navigate("/signup");
  };

  const handleForgotPassword = () => {
    navigate("/forgot");
  };

  const initialValues = {
    username: "",
    password: "",
    keepLoggedIn: false,
  };

  // Shared post-authentication step: fetch profile and hydrate the store.
  const finishLogin = async (tokens, keepLoggedIn) => {
    const userDetails = await getUserDetails({
      region: process.env.REACT_APP_AWS_REGION,
      accessToken: tokens.accessToken,
    });

    // Store JWT tokens for socket authentication
    const userData = {
      username: userDetails.attributes.email,
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      keepLoggedIn,
      tokenRefreshedAt: Date.now(), // Track when tokens were created
      firstName: userDetails.attributes.given_name || "",
      lastName: userDetails.attributes.family_name || "",
    };
    dispatch(getUser(userData));
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: initialValues,
      validationSchema: LogIn,
      onSubmit: async ({ username, password, keepLoggedIn }) => {
        dispatch(updateLoading(true));
        dispatch(updateLoadingMsg("Authenticating..."));

        try {
          // First try to authenticate with the new Cognito pool
          const result = await signInNew(username, password);

          // Password OK but the account has MFA: move to the code step.
          if (result.status === "MFA_REQUIRED") {
            dispatch(updateLoading(false));
            setMfaCode("");
            setMfaChallenge({
              session: result.session,
              username: result.username,
              challengeName: result.challengeName,
              keepLoggedIn,
            });
            return;
          }

          await finishLogin(result, keepLoggedIn);
        } catch (error) {
          dispatch(updateLoading(false));
          console.error("Authentication failed:", error);

          let errorMessage = "Incorrect username or password";
          if (error.code === "UserNotConfirmedException") {
            errorMessage =
              "User account is not confirmed. Please check your email.";
          } else if (error.code === "PasswordResetRequiredException") {
            errorMessage =
              "Password reset required. Please reset your password.";
          } else if (error.code === "UserNotFoundException") {
            errorMessage = "User not found. Please check your email.";
          } else if (error.message) {
            errorMessage = error.message;
          }

          toast.error(errorMessage);
        }
      },
    });

  const handleVerifyMfa = async () => {
    const code = mfaCode.trim();
    if (!/^\d{6}$/.test(code)) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }

    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Verifying code..."));

    try {
      const tokens = await respondMfaNew({
        username: mfaChallenge.username,
        session: mfaChallenge.session,
        code,
        challengeName: mfaChallenge.challengeName,
      });

      await finishLogin(tokens, mfaChallenge.keepLoggedIn);
    } catch (error) {
      dispatch(updateLoading(false));
      console.error("MFA verification failed:", error);
      toast.error(error.message || "Invalid code. Please try again.");

      // If the session died, drop back to the password step.
      if (/session has expired/i.test(error.message || "")) {
        setMfaChallenge(null);
      }
    }
  };

  const handleCancelMfa = () => {
    setMfaChallenge(null);
    setMfaCode("");
    setUsingBackupCode(false);
    setBackupCode("");
  };

  const handleUseBackupCode = async () => {
    const code = backupCode.trim();
    if (!code) {
      toast.error("Please enter a backup code.");
      return;
    }

    setBackupSubmitting(true);
    try {
      const result = await recover2fa(mfaChallenge.username, code);
      if (!result.success) {
        toast.error(result.message || "Invalid backup code. Please try again.");
        return;
      }
      toast.success("2FA has been disabled. Please sign in again with your password.");
      setMfaChallenge(null);
      setUsingBackupCode(false);
      setBackupCode("");
    } catch (error) {
      toast.error(error.message || "Invalid backup code. Please try again.");
    } finally {
      setBackupSubmitting(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ThemeProvider theme={authTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: authTheme.palette.background.default,
          display: "flex",
          zoom: 0.75,
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem 1rem",
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: "2rem 1.5rem", sm: "3rem 2.5rem" },
              borderRadius: 3,
              backgroundColor: authTheme.palette.background.paper,
              boxShadow:
                authTheme.palette.mode === "dark"
                  ? "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
                  : "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <AuthLogo />
            </Box>

            {mfaChallenge ? (
              /* ---- MFA code step ---- */
              <Box>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: "2rem", sm: "2.5rem" },
                      fontWeight: 700,
                      color: authTheme.palette.text.primary,
                      mb: 1,
                    }}
                  >
                    Two-Factor Authentication
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: authTheme.palette.text.secondary,
                      fontSize: "1.125rem",
                    }}
                  >
                    Enter the 6-digit code from your authenticator app.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  variant="outlined"
                  id="mfaCode"
                  name="mfaCode"
                  label="Authentication code"
                  placeholder="123456"
                  value={mfaCode}
                  onChange={(e) =>
                    setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleVerifyMfa();
                    }
                  }}
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                  autoFocus
                  sx={{ mb: 3 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleVerifyMfa}
                  sx={{
                    mb: 2,
                    height: "56px",
                    fontSize: "1rem",
                    fontWeight: 600,
                    backgroundColor:
                      authTheme.palette.mode === "dark" ? "#F9FAFB" : "#1F2937",
                    color:
                      authTheme.palette.mode === "dark" ? "#ffffff" : "#FFFFFF",
                    "&:hover": {
                      backgroundColor:
                        authTheme.palette.mode === "dark"
                          ? "#E5E7EB"
                          : "#374151",
                    },
                  }}
                >
                  Verify
                </Button>

                {!usingBackupCode ? (
                  <Box sx={{ textAlign: "center" }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setUsingBackupCode(true)}
                      sx={{
                        color: authTheme.palette.text.secondary,
                        textDecoration: "none",
                        fontSize: "0.875rem",
                        "&:hover": { textDecoration: "underline" },
                        display: "block",
                        mb: 1,
                      }}
                    >
                      Lost access to your authenticator app?
                    </Link>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={handleCancelMfa}
                      sx={{
                        color: authTheme.palette.secondary.main,
                        textDecoration: "none",
                        fontWeight: 500,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Back to sign in
                    </Link>
                  </Box>
                ) : (
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: authTheme.palette.text.secondary, mb: 1.5 }}
                    >
                      Enter one of your backup codes to disable 2FA and regain access. You'll need to set up 2FA again afterwards.
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Backup code"
                      placeholder="e.g. a1b2c3d4"
                      value={backupCode}
                      onChange={(e) => setBackupCode(e.target.value.replace(/\s/g, "").toLowerCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleUseBackupCode(); } }}
                      inputProps={{ maxLength: 8 }}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleUseBackupCode}
                      disabled={backupSubmitting || backupCode.length < 8}
                      sx={{
                        mb: 2,
                        height: "56px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        backgroundColor:
                          authTheme.palette.mode === "dark" ? "#F9FAFB" : "#1F2937",
                        color:
                          authTheme.palette.mode === "dark" ? "#ffffff" : "#FFFFFF",
                        "&:hover": {
                          backgroundColor:
                            authTheme.palette.mode === "dark" ? "#E5E7EB" : "#374151",
                        },
                      }}
                    >
                      {backupSubmitting ? "Verifying…" : "Use Backup Code"}
                    </Button>
                    <Box sx={{ textAlign: "center" }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => { setUsingBackupCode(false); setBackupCode(""); }}
                        sx={{
                          color: authTheme.palette.secondary.main,
                          textDecoration: "none",
                          fontWeight: 500,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        Back to authenticator code
                      </Link>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <>
            {/* Welcome Text */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem" },
                  fontWeight: 700,
                  color: authTheme.palette.text.primary,
                  mb: 1,
                }}
              >
                Welcome Back!
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: authTheme.palette.text.secondary,
                  fontSize: "1.125rem",
                }}
              >
                Let's get you signed in securely.
              </Typography>
            </Box>

            {/* Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 3 }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            >
              {/* Email Field */}
              <TextField
                fullWidth
                variant="outlined"
                id="username"
                name="username"
                label="Email"
                placeholder="Enter Your Email Address"
                value={values.username}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.username && Boolean(errors.username)}
                helperText={touched.username && errors.username}
                sx={{ mb: 3 }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                variant="outlined"
                id="password"
                name="password"
                label="Password"
                placeholder="Your Password"
                type={showPassword ? "text" : "password"}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.password && Boolean(errors.password)}
                helperText={touched.password && errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{ color: authTheme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      id="keepLoggedIn"
                      name="keepLoggedIn"
                      checked={values.keepLoggedIn}
                      onChange={handleChange}
                      sx={{
                        color: authTheme.palette.text.secondary,
                        "&.Mui-checked": {
                          color: authTheme.palette.secondary.main,
                        },
                      }}
                    />
                  }
                  label="Keep Me Logged In"
                  sx={{
                    m: 0,
                    color: authTheme.palette.text.secondary,
                  }}
                />

                <Link
                  component="button"
                  variant="body2"
                  onClick={handleForgotPassword}
                  sx={{
                    color: authTheme.palette.secondary.main,
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Forgot Your Password?
                </Link>
              </Box>

              {/* Login Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  mb: 3,
                  height: "56px",
                  fontSize: "1rem",
                  fontWeight: 600,
                  backgroundColor:
                    authTheme.palette.mode === "dark" ? "#F9FAFB" : "#1F2937",
                  color:
                    authTheme.palette.mode === "dark" ? "#ffffff" : "#FFFFFF",
                  "&:hover": {
                    backgroundColor:
                      authTheme.palette.mode === "dark" ? "#E5E7EB" : "#374151",
                  },
                }}
              >
                Log into your account
              </Button>

              {/* Sign Up Link */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ color: authTheme.palette.text.secondary }}
                >
                  Don't Have an Account?{" "}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleSignUpClick}
                    sx={{
                      color: authTheme.palette.secondary.main,
                      textDecoration: "none",
                      fontWeight: 600,
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Sign Up
                  </Link>
                </Typography>
              </Box>
            </Box>
              </>
            )}
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default SignIn;
